import type { ReactNode } from 'react';
import { ChartNoAxesColumn, type LucideIcon } from 'lucide-react';
import { fmtNum } from '../util/format';

export type ColorGrafico = 'green' | 'amber' | 'red' | 'muted' | 'brand';

const VAR: Record<ColorGrafico, string> = {
  green: 'var(--green)',
  amber: 'var(--amber)',
  red: 'var(--red)',
  muted: 'var(--muted)',
  brand: 'var(--brand)',
};

/** Tarjeta de gráfico: título, unidad y estado vacío. */
export function TarjetaGrafico({ titulo, unidad, vacio, pie, children }: { titulo: string; unidad: string; vacio: boolean; pie?: string; children: ReactNode }) {
  return (
    <section className="card" aria-label={titulo}>
      <header>
        <h2>{titulo}</h2>
        <span className="grafico-unidad">{unidad}</span>
      </header>
      <div className="body">
        {vacio ? (
          <div className="vacio">
            <ChartNoAxesColumn aria-hidden />
            No hay datos para mostrar.
          </div>
        ) : (
          children
        )}
      </div>
      {pie && !vacio && <div className="foot">{pie}</div>}
    </section>
  );
}

export interface DatoBarra {
  etiqueta: string;
  valor: number;
  color?: ColorGrafico;
  /** Texto extra junto al valor (p. ej. un ícono de aviso en texto). */
  nota?: string;
}

/** Barras horizontales en SVG. */
export function BarrasHorizontales({ datos, decimales = 0, sufijo = '' }: { datos: DatoBarra[]; decimales?: number; sufijo?: string }) {
  const max = Math.max(...datos.map((d) => d.valor), 1);
  return (
    <div className="barras" role="list">
      {datos.map((d) => (
        <div className={`barra-fila ${d.color === 'amber' ? 'ambar' : ''}`} key={d.etiqueta} role="listitem">
          <span className="et" title={d.etiqueta}>
            {d.etiqueta}
          </span>
          <svg viewBox="0 0 100 12" preserveAspectRatio="none" role="img" aria-label={`${d.etiqueta}: ${fmtNum(d.valor, decimales)}${sufijo}`}>
            <rect x="0" y="0" width="100" height="12" style={{ fill: 'var(--raised)' }} />
            <rect x="0" y="0" width={(d.valor / max) * 100} height="12" style={{ fill: VAR[d.color ?? 'brand'] }} />
          </svg>
          <span className="val">
            {fmtNum(d.valor, decimales)}
            {sufijo}
            {d.nota ? ` ${d.nota}` : ''}
          </span>
        </div>
      ))}
    </div>
  );
}

export interface Segmento {
  etiqueta: string;
  valor: number;
  color: ColorGrafico;
  icono: LucideIcon;
}

/** Barra apilada con leyenda que repite el color con ícono y texto. */
export function BarraApilada({ segmentos }: { segmentos: Segmento[] }) {
  const total = segmentos.reduce((s, x) => s + x.valor, 0) || 1;
  let acumulado = 0;
  return (
    <div>
      <svg className="apilada" viewBox="0 0 100 10" preserveAspectRatio="none" role="img" aria-label={segmentos.map((s) => `${s.etiqueta}: ${s.valor}`).join(', ')}>
        {segmentos.map((s) => {
          const ancho = (s.valor / total) * 100;
          const x = acumulado;
          acumulado += ancho;
          return <rect key={s.etiqueta} x={x} y="0" width={ancho} height="10" style={{ fill: VAR[s.color] }} />;
        })}
      </svg>
      <div className="leyenda-graf">
        {segmentos.map((s) => (
          <span key={s.etiqueta} className={s.color === 'brand' ? 'green' : s.color}>
            <s.icono aria-hidden />
            {s.etiqueta}: <b>{fmtNum(s.valor)}</b> ({fmtNum((s.valor / total) * 100, 0)} %)
          </span>
        ))}
      </div>
    </div>
  );
}
